import React, { useEffect, useRef, useState } from 'react';
import './BlogSection.css';

const ARTICLES = [
  {
    id: 1,
    category: 'Cold Outreach',
    title: 'How to cold email a recruiter (with templates that actually work)',
    description:
      'The exact email structure, subject lines, and follow-up timing that gets responses from FAANG recruiters.',
    gradient: 1,
    readTime: '5 min read',
  },
  {
    id: 2,
    category: 'Career Planning',
    title: 'The 2026 internship application timeline \u2014 when to apply to top companies',
    description:
      'Month-by-month deadlines for Google, Meta, Apple, and 50+ companies \u2014 plus early application strategies.',
    gradient: 2,
    readTime: '5 min read',
  },
  {
    id: 3,
    category: 'Interview Prep',
    title: 'You got the interview. Here\u2019s how to prepare in 48 hours.',
    description:
      'A 48-hour prep framework covering research, behavioral questions, and the one thing most candidates forget.',
    gradient: 3,
    readTime: '5 min read',
  },
];

/* ---- SVG Icons ---- */

function EnvelopeIcon() {
  return (
    <svg
      className="blog-card__thumbnail-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="M22 4L12 13L2 4" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg
      className="blog-card__thumbnail-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function MicrophoneIcon() {
  return (
    <svg
      className="blog-card__thumbnail-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="9" y="1" width="6" height="12" rx="3" />
      <path d="M5 10a7 7 0 0 0 14 0" />
      <line x1="12" y1="17" x2="12" y2="21" />
      <line x1="8" y1="21" x2="16" y2="21" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg
      className="blog-card__read-time-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

const THUMB_ICONS = [EnvelopeIcon, CalendarIcon, MicrophoneIcon];

function BlogSection() {
  const [visible, setVisible] = useState([false, false, false]);
  const gridRef = useRef(null);

  useEffect(() => {
    const prefersReduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (prefersReduced) {
      setVisible([true, true, true]);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible([true, true, true]);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.15 }
    );

    if (gridRef.current) {
      observer.observe(gridRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section className="blog-section">
      <div className="blog-section__inner">
        {/* Header */}
        <div className="blog-section__header">
          <div>
            <p className="blog-section__kicker">RESOURCES</p>
            <h2 className="blog-section__title">Level up your job search.</h2>
          </div>
          <a href="/blog" className="blog-section__view-all">
            View all articles{' '}
            <span className="blog-section__view-all-arrow">&rarr;</span>
          </a>
        </div>

        {/* Card grid */}
        <div className="blog-section__grid" ref={gridRef}>
          {ARTICLES.map((article, idx) => {
            const Icon = THUMB_ICONS[idx];
            const animClass = `blog-card blog-card--animate blog-card--delay-${idx}${
              visible[idx] ? ' blog-card--visible' : ''
            }`;

            return (
              <article key={article.id} className={animClass}>
                {/* Thumbnail */}
                <div className="blog-card__thumbnail">
                  <div
                    className={`blog-card__thumbnail-bg blog-card__thumbnail-bg--${article.gradient}`}
                  >
                    <Icon />
                  </div>
                </div>

                {/* Body */}
                <div className="blog-card__body">
                  <span className="blog-card__category">
                    {article.category}
                  </span>
                  <h3 className="blog-card__title">{article.title}</h3>
                  <p className="blog-card__desc">{article.description}</p>

                  {/* Meta */}
                  <div className="blog-card__meta">
                    <span className="blog-card__read-time">
                      <ClockIcon />
                      {article.readTime}
                    </span>
                    <span className="blog-card__read-more">
                      Read more{' '}
                      <span className="blog-card__read-more-arrow">
                        &rarr;
                      </span>
                    </span>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default BlogSection;
